/**
 * COMMERCE-176B-R2-R2 / COMMERCE-176D-R1 / BANK-IBAN-R3.9 / R7.2.2D
 * Single customer-visible price resolver (public currency = UAH).
 * BANK_CANARY_AMOUNT cannot write Product.price / Catalog.price / SearchResult.price / PDP.price / Cart.price.
 * Ordinary retail >= 1 UAH uses ceil_whole_uah payable policy.
 */
import type { CatalogV2Product } from "./catalog-v2-types.js";
import { resolveDisplayPrice, type DisplayPriceResult } from "./sale-pricing-v2.js";
import { getPromoOverlayPrices } from "./promo-price-overlay-v2.js";
import { formatUah, getCustomerSalePrice } from "./money-v2.js";

/** Kept as a name for existing guards; overlay is permanently disabled in R3.9. */
const OWNER_CANARY_PRICE_ALLOWLIST = new Set(["475734587"]);

export function isOwnerCanaryPriceViewer(): boolean {
  void OWNER_CANARY_PRICE_ALLOWLIST;
  return false;
}

function applyPromoOverlay(
  product: CatalogV2Product,
  base: DisplayPriceResult,
): DisplayPriceResult {
  if (base.saleActive) return base;
  const overlay = getPromoOverlayPrices(product.id);
  if (!overlay) return base;
  const oldRaw = overlay.old_price_uah;
  const priceRaw = typeof product.priceUah === "number" ? product.priceUah : overlay.price_uah;
  const old = oldRaw != null ? getCustomerSalePrice(oldRaw) : null;
  const price = getCustomerSalePrice(priceRaw);
  if (old == null || !(old > price) || !(price > 0)) return base;
  const savings = Math.max(0, old - price);
  const pct = old > 0 ? Math.round((savings / old) * 100) : 0;
  return {
    primary: price,
    regular: old,
    sale: price,
    saleActive: true,
    badge: pct > 0 ? `−${pct}%` : "Sale",
    savings,
    promotionId: base.promotionId ?? "promotions-v1",
  };
}

function withPayablePrimary(result: DisplayPriceResult): DisplayPriceResult {
  const primary = getCustomerSalePrice(result.primary);
  const regular =
    result.regular != null ? getCustomerSalePrice(result.regular) : null;
  const sale = result.sale != null ? getCustomerSalePrice(result.sale) : null;
  const savings =
    regular != null && primary > 0 && regular > primary ? Math.max(0, regular - primary) : result.savings;
  return {
    ...result,
    primary,
    regular,
    sale,
    savings,
  };
}

/**
 * Canonical customer-facing price for product cards, search hits, PDP, cart, checkout.
 * R7.2.2D — always returns payable whole-UAH for ordinary retail >= 1.
 */
export function resolveCustomerVisiblePrice(
  product: CatalogV2Product,
  now = new Date(),
): DisplayPriceResult {
  return withPayablePrimary(applyPromoOverlay(product, resolveDisplayPrice(product, now)));
}

/** Numeric UAH for cart add / search card mapping. */
export function resolveCustomerVisiblePriceUah(product: CatalogV2Product, now = new Date()): number {
  return resolveCustomerVisiblePrice(product, now).primary;
}

/**
 * BANK-IBAN-R3.10 / R7.2.2D — one commercial sell price for search / catalog / PDP / cart / checkout.
 */
export function resolveCustomerSellPrice(product: CatalogV2Product, now = new Date()): number {
  return getCustomerSalePrice(resolveCustomerVisiblePriceUah(product, now));
}

export function mergePdpHydrationKeepingCommercialPrice(
  seed: CatalogV2Product | null | undefined,
  hydrated: CatalogV2Product | null | undefined,
): CatalogV2Product | null {
  if (!seed && !hydrated) return null;
  if (!hydrated) return seed ?? null;
  if (!seed) return hydrated;
  const seedPrice = typeof seed.priceUah === "number" ? seed.priceUah : null;
  const hydPrice = typeof hydrated.priceUah === "number" ? hydrated.priceUah : null;
  const raw =
    seedPrice != null && seedPrice >= 1 ? seedPrice : hydPrice != null && hydPrice >= 1 ? hydPrice : (seedPrice ?? hydPrice);
  const priceUah = raw != null ? getCustomerSalePrice(raw) : null;
  return {
    ...seed,
    ...hydrated,
    priceUah,
    displayPrice: typeof priceUah === "number" ? formatUah(priceUah) : seed.displayPrice ?? hydrated.displayPrice,
    isPriced: typeof priceUah === "number" && priceUah > 0,
  };
}

/** Invariant helper — bank canary amount must never become Product.price. */
export function bankCanaryCannotWriteProductPrice(nextUah: number, canonicalUah: number): number {
  if (nextUah < 1 && canonicalUah >= 1) return getCustomerSalePrice(canonicalUah);
  return getCustomerSalePrice(canonicalUah);
}
